<template>
  <div class="influencer-ask">
    <div class="page-header">
      <a class="back-link" href="#/influencers">
        <i class="bi bi-arrow-left" aria-hidden="true"></i>
        Back to influencers
      </a>

      <div class="title-row">
        <div>
          <h1 class="page-title">
            Ask AI
            <span class="title-accent">about this influencer</span>
          </h1>
          <p class="page-subtitle">
            Ask any question. Perplexity will pull in influencer data and optionally recent Instagram / TikTok posts as context.
          </p>
        </div>
        <a class="btn-edit-influencer" :href="'#/influencer/edit/' + influencerId" v-if="influencerId">
          <i class="bi bi-pencil" aria-hidden="true"></i>
          Edit influencer
        </a>
      </div>
    </div>

    <section class="influencer-card" :class="{ 'is-loading': profileLoading }">
      <div class="profile-loading" v-if="profileLoading">
        <div class="skeleton skeleton-avatar"></div>
        <div class="skeleton-meta">
          <div class="skeleton skeleton-line skeleton-line-lg"></div>
          <div class="skeleton skeleton-line skeleton-line-sm"></div>
        </div>
      </div>
      <template v-else>
        <div class="avatar" :style="avatarStyle">{{ avatarInitial }}</div>
        <div class="profile-meta">
          <div class="profile-row">
            <h2 class="profile-name">{{ influencer.name || '—' }}</h2>
            <span class="id-badge">#{{ influencer.id }}</span>
          </div>
          <div class="profile-row-sub">
            <span class="profile-nick" v-if="influencer.nickname">@{{ influencer.nickname }}</span>
            <span class="profile-realname" v-if="fullName">{{ fullName }}</span>
            <span class="profile-loc" v-if="influencer.location">
              <i class="bi bi-geo-alt-fill" aria-hidden="true"></i>
              {{ influencer.location }}
            </span>
            <span class="profile-lang" v-if="influencer.lang && influencer.lang !== 'un'">{{ String(influencer.lang).toUpperCase() }}</span>
          </div>
          <div class="profile-channels" v-if="authors.length">
            <a
              v-for="(a, i) in authors"
              :key="'ch' + i"
              class="channel-pill"
              :href="a.url"
              target="_blank"
              rel="noopener"
            >
              <img v-if="detectPlatform(a.url)" :src="platformIconSrc(detectPlatform(a.url))" :alt="detectPlatform(a.url)"/>
              <span>{{ extractUsername(a.url) || a.url }}</span>
              <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
            </a>
          </div>
        </div>
      </template>
    </section>

    <section class="composer">
      <div class="composer-head">
        <div class="composer-icon">
          <i class="bi bi-stars" style="font-size:18px" aria-hidden="true"></i>
        </div>
        <div>
          <h3 class="composer-title">Your question</h3>
          <p class="composer-desc">Describe what you want to know — a campaign fit, audience analysis, content style, anything.</p>
        </div>
      </div>

      <div class="composer-body">
        <textarea
          v-model="prompt"
          class="composer-input"
          :placeholder="examplePrompts[examplePromptIdx]"
          rows="3"
          @keydown.meta.enter="ask"
          @keydown.ctrl.enter="ask"
        ></textarea>

        <div class="quick-prompts">
          <button
            v-for="(ex, idx) in examplePrompts"
            :key="'ex' + idx"
            type="button"
            class="quick-prompt"
            @click="prompt = ex"
            :title="'Use: ' + ex"
          >{{ ex }}</button>
        </div>

        <div class="settings-grid">
          <div class="settings-block">
            <label class="settings-label">
              Model
              <span class="model-cost" :class="'cost-' + currentModel.cost">{{ currentModel.costLabel }}</span>
            </label>
            <div class="model-options">
              <label
                v-for="m in models"
                :key="m.value"
                class="model-option"
                :class="{ 'is-active': model === m.value }"
              >
                <input type="radio" :value="m.value" v-model="model">
                <span class="model-option-name">{{ m.label }}</span>
                <span class="model-option-badge" v-if="m.badge">{{ m.badge }}</span>
              </label>
            </div>
            <p class="settings-hint">{{ currentModel.description }}</p>
          </div>

          <div class="settings-block">
            <label class="settings-label">Context</label>
            <div class="toggle-row">
              <label class="toggle" :class="{ 'is-disabled': !hasInstagram }">
                <input type="checkbox" v-model="includeInstagram" :disabled="!hasInstagram">
                <span class="toggle-track"><span class="toggle-thumb"></span></span>
                <span class="toggle-text">
                  <img class="toggle-icon" :src="platformIconSrc('instagram')" alt="instagram"/>
                  Include Instagram posts
                  <span class="muted" v-if="!hasInstagram">— no account linked</span>
                </span>
              </label>
              <label class="toggle" :class="{ 'is-disabled': !hasTiktok }">
                <input type="checkbox" v-model="includeTiktok" :disabled="!hasTiktok">
                <span class="toggle-track"><span class="toggle-thumb"></span></span>
                <span class="toggle-text">
                  <img class="toggle-icon" :src="platformIconSrc('tiktok')" alt="tiktok"/>
                  Include TikTok posts
                  <span class="muted" v-if="!hasTiktok">— no account linked</span>
                </span>
              </label>
            </div>

            <div class="posts-limit" v-if="includeInstagram || includeTiktok">
              <label class="settings-label settings-label-sm">Posts limit per platform: <strong>{{ postsLimit }}</strong></label>
              <input type="range" min="1" max="50" step="1" v-model.number="postsLimit" class="slider">
              <div class="slider-ticks">
                <span>1</span>
                <span>10</span>
                <span>25</span>
                <span>50</span>
              </div>
            </div>
          </div>
        </div>

        <div class="composer-foot">
          <span class="composer-tip">
            <kbd>Ctrl</kbd> + <kbd>Enter</kbd> to send
          </span>
          <button class="btn-ask" :disabled="!canAsk || loading" @click="ask">
            <span v-if="!loading" class="btn-content">
              <i class="bi bi-send-fill" aria-hidden="true"></i>
              Ask AI
            </span>
            <span v-else class="btn-content">
              <span class="spinner"></span>
              Thinking…
            </span>
          </button>
        </div>
      </div>
    </section>

    <section class="conversation" v-if="history.length || loading">
      <h3 class="conversation-title">
        <i class="bi bi-chat" aria-hidden="true"></i>
        Conversation
        <span class="conversation-count" v-if="history.length">{{ history.length }}</span>
      </h3>

      <div class="history">
        <article class="qa" v-for="(item, idx) in history" :key="'qa' + idx">
          <header class="qa-question">
            <div class="qa-bubble qa-bubble-user">
              <span class="qa-label">You</span>
              <p>{{ item.question }}</p>
            </div>
            <div class="qa-meta">
              <span class="qa-model">{{ item.model }}</span>
              <span class="qa-meta-sep" v-if="item.usage">·</span>
              <span class="qa-tokens" v-if="item.usage">{{ item.usage.total_tokens }} tokens</span>
              <span class="qa-meta-sep" v-if="item.usage && item.usage.cost && item.usage.cost.total_cost">·</span>
              <span class="qa-cost" v-if="item.usage && item.usage.cost && item.usage.cost.total_cost">${{ item.usage.cost.total_cost.toFixed(4) }}</span>
              <span class="qa-meta-sep">·</span>
              <span class="qa-ctx" v-if="item.context.length">{{ item.context.join(', ') }}</span>
              <span class="qa-ctx muted" v-else>profile only</span>
            </div>
          </header>

          <div class="qa-bubble qa-bubble-ai" v-if="item.error">
            <span class="qa-label qa-label-err">Error</span>
            <p class="qa-error-text">{{ item.error }}</p>
          </div>
          <div class="qa-bubble qa-bubble-ai" v-else>
            <span class="qa-label">AI</span>
            <div class="qa-answer" v-html="renderAnswer(item.answer)"></div>
            <div class="qa-citations" v-if="item.citations && item.citations.length">
              <span class="qa-citations-label">Sources</span>
              <ol>
                <li v-for="(c, ci) in item.citations" :key="'cit' + idx + '-' + ci">
                  <a :href="c" target="_blank" rel="noopener">{{ shortenUrl(c) }}</a>
                </li>
              </ol>
            </div>
          </div>
        </article>

        <article class="qa qa-pending" v-if="loading">
          <header class="qa-question">
            <div class="qa-bubble qa-bubble-user">
              <span class="qa-label">You</span>
              <p>{{ pendingQuestion }}</p>
            </div>
          </header>
          <div class="qa-bubble qa-bubble-ai qa-thinking">
            <span class="qa-label">AI</span>
            <div class="thinking-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
  import { detectPlatformHint } from '../../utils/socialChannels';

  const MODELS = [
    { value: 'sonar',                label: 'Sonar',              badge: 'Fast',    cost: 'low',     costLabel: '$',    description: 'Quick, economical search — fewer sources, shorter answer.' },
    { value: 'sonar-pro',            label: 'Sonar Pro',          badge: '',        cost: 'medium',  costLabel: '$$',   description: 'Advanced search with more sources and longer answers.' },
    { value: 'sonar-reasoning',      label: 'Sonar Reasoning',    badge: 'CoT',     cost: 'medium',  costLabel: '$$',   description: 'Search combined with Chain-of-Thought reasoning.' },
    { value: 'sonar-reasoning-pro',  label: 'Sonar Reasoning Pro', badge: 'Default', cost: 'high',   costLabel: '$$$',  description: 'Higher-quality reasoning and inference for harder lookups.' },
    { value: 'sonar-deep-research',  label: 'Sonar Deep Research', badge: 'Deep',   cost: 'highest', costLabel: '$$$$', description: 'In-depth research report. Highest cost and longest response time.' }
  ];

  const AVATAR_PALETTE = [
    'linear-gradient(135deg, #265a88 0%, #833ab4 100%)',
    'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
    'linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)',
    'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
    'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
    'linear-gradient(135deg, #f43f5e 0%, #a855f7 100%)'
  ];

  export default {
    name: 'influencer-ask',
    data() {
      return {
        apiHost: this.$appSettings.getSetting('host'),
        influencer: {},
        authors: [],
        profileLoading: true,
        models: MODELS,
        prompt: '',
        model: 'sonar-reasoning-pro',
        includeInstagram: false,
        includeTiktok: false,
        postsLimit: 10,
        loading: false,
        pendingQuestion: '',
        history: [],
        examplePrompts: [
          'Czy ten influencer pasuje do kampanii marki kosmetycznej premium?',
          'Opisz styl komunikacji i najczęstsze tematy poruszane przez tego influencera.',
          'Jaka jest grupa docelowa tego influencera?',
          'Czy w ostatnim czasie influencer brał udział w kontrowersjach?'
        ],
        examplePromptIdx: 0
      };
    },
    computed: {
      influencerId() {
        return parseInt(this.$route.params.id, 10);
      },
      fullName() {
        return [this.influencer.firstname, this.influencer.lastname].filter(Boolean).join(' ');
      },
      hasInstagram() {
        return this.authors.some(a => (detectPlatformHint(a.url) || '') === 'instagram');
      },
      hasTiktok() {
        return this.authors.some(a => (detectPlatformHint(a.url) || '') === 'tiktok');
      },
      canAsk() {
        return (this.prompt || '').trim().length > 0;
      },
      currentModel() {
        return this.models.find(m => m.value === this.model) || this.models[1];
      },
      avatarInitial() {
        const src = (this.influencer.nickname || this.influencer.name || '?').trim();
        return src.charAt(0).toUpperCase();
      },
      avatarStyle() {
        const id = this.influencerId || 0;
        const grad = AVATAR_PALETTE[id % AVATAR_PALETTE.length];
        return { background: grad };
      }
    },
    watch: {
      hasInstagram(now) { if (now && !this.includeInstagram) this.includeInstagram = true; },
      hasTiktok(now) { if (now && !this.includeTiktok) this.includeTiktok = true; }
    },
    created() {
      this.examplePromptIdx = Math.floor(Math.random() * this.examplePrompts.length);
      this.loadInfluencer();
    },
    methods: {
      loadInfluencer() {
        this.profileLoading = true;
        this.$http.get(this.apiHost + 'influencers/show', {
          params: { token: this.$auth.getToken(), id: this.influencerId }
        }).then(response => {
          if (response.body && response.body.success) {
            this.influencer = response.body.data.influencer || {};
            this.authors = response.body.data.author || [];
            this.includeInstagram = this.hasInstagram;
            this.includeTiktok = this.hasTiktok;
          } else {
            this.$notify({ group: 'default', type: 'error', text: 'Could not load influencer.' });
          }
        }, () => {
          this.$notify({ group: 'default', type: 'error', text: 'Could not load influencer.' });
        }).finally(() => {
          this.profileLoading = false;
        });
      },
      ask() {
        if (!this.canAsk || this.loading) return;
        const question = this.prompt.trim();
        this.pendingQuestion = question;
        this.loading = true;

        const body = {
          influencer_id: this.influencerId,
          prompt: question,
          include_instagram_posts: !!this.includeInstagram,
          include_tiktok_posts: !!this.includeTiktok,
          posts_limit: this.postsLimit,
          model: this.model
        };
        const usedModel = this.model;
        const ctx = [];
        if (this.includeInstagram) ctx.push('IG posts');
        if (this.includeTiktok) ctx.push('TT posts');

        this.$http.post(this.apiHost + 'perplexity/influencer/ask', body, {
          headers: { Authorization: 'Bearer ' + this.$auth.getToken() },
          params: { token: this.$auth.getToken() }
        }).then(response => {
          const data = response.body || {};
          if (data.success) {
            this.history.push({
              question,
              answer: data.answer || '',
              citations: data.citations || [],
              usage: data.usage || null,
              model: usedModel,
              context: ctx
            });
            this.prompt = '';
            this.$nextTick(this.scrollToLatest);
          } else {
            this.pushError(question, usedModel, ctx, data.error || 'Empty response');
          }
        }, response => {
          const data = (response && response.body) || {};
          let msg = data.error;
          if (!msg) {
            if (response && response.status === 400) msg = 'Missing required parameters';
            else if (response && response.status === 404) msg = 'Influencer not found';
            else if (response && response.status) msg = 'HTTP ' + response.status;
            else msg = 'Network error';
          }
          this.pushError(question, usedModel, ctx, typeof msg === 'string' ? msg : JSON.stringify(msg));
        }).finally(() => {
          this.loading = false;
          this.pendingQuestion = '';
        });
      },
      pushError(question, model, ctx, error) {
        this.history.push({ question, answer: '', citations: [], usage: null, model, context: ctx, error });
        this.$nextTick(this.scrollToLatest);
      },
      scrollToLatest() {
        const list = document.querySelectorAll('.history .qa');
        if (!list.length) return;
        const last = list[list.length - 1];
        if (last && last.scrollIntoView) last.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      detectPlatform(url) {
        return detectPlatformHint(url) || '';
      },
      platformIconSrc(platform) {
        try { return require('../../assets/platforms/' + platform + '.png'); }
        catch (e) { return ''; }
      },
      extractUsername(url) {
        if (!url) return '';
        const stripped = url.replace(/[?#].*$/, '').replace(/\/$/, '');
        const match = stripped.match(/\/@?([A-Za-z0-9._-]+)\/?$/);
        return match ? '@' + match[1] : '';
      },
      shortenUrl(url) {
        try {
          const u = new URL(url);
          const path = u.pathname.length > 36 ? u.pathname.slice(0, 33) + '…' : u.pathname;
          return u.host + (path === '/' ? '' : path);
        } catch (e) {
          return url;
        }
      },
      escapeHtml(s) {
        return String(s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      },
      renderAnswer(raw) {
        if (!raw) return '';
        let s = this.escapeHtml(raw);
        s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
        s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
        s = s.replace(/\[(\d+)\]/g, '<sup class="cit-ref">[$1]</sup>');
        s = s.replace(/(^|\s)(https?:\/\/[^\s<]+)/g, '$1<a href="$2" target="_blank" rel="noopener">$2</a>');
        return s;
      }
    }
  }
</script>

<style scoped>
  .influencer-ask {
    max-width: 920px;
    margin: 0 auto 80px;
    color: #1f2937;
  }

  .page-header { margin-bottom: 20px; }
  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 14px;
    text-decoration: none;
    transition: color 0.15s;
  }
  .back-link:hover { color: #265a88; }

  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
  }
  .page-title {
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0 0 6px;
    color: #0f172a;
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }
  .title-accent {
    background: linear-gradient(135deg, #265a88 0%, #833ab4 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    font-size: 20px;
    font-weight: 600;
  }
  .page-subtitle {
    color: #6b7280;
    font-size: 14px;
    margin: 0;
    max-width: 640px;
  }
  .btn-edit-influencer {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #475569;
    padding: 8px 14px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    transition: border-color 0.15s, color 0.15s;
    flex-shrink: 0;
  }
  .btn-edit-influencer:hover {
    border-color: #265a88;
    color: #265a88;
  }

  .influencer-card {
    margin-top: 24px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 20px 22px;
    display: flex;
    gap: 18px;
    align-items: center;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .avatar {
    flex: 0 0 64px;
    width: 64px;
    height: 64px;
    border-radius: 18px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.01em;
    box-shadow: 0 6px 16px rgba(38, 90, 136, 0.18);
  }
  .profile-meta { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
  .profile-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .profile-name { font-size: 22px; font-weight: 700; color: #0f172a; margin: 0; letter-spacing: -0.01em; }
  .id-badge {
    background: #f1f5f9;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 6px;
  }
  .profile-row-sub {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 14px;
    color: #64748b;
    font-size: 13px;
  }
  .profile-nick { color: #265a88; font-weight: 600; }
  .profile-realname { color: #334155; }
  .profile-loc { display: inline-flex; align-items: center; gap: 4px; }
  .profile-loc .bi { color: #6b7280; }
  .profile-lang {
    background: #f3f4f6;
    color: #1f2937;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 999px;
    letter-spacing: 0.04em;
  }
  .profile-channels {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
  }
  .channel-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 4px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    font-size: 12.5px;
    color: #0f172a;
    text-decoration: none;
    max-width: 220px;
    transition: border-color 0.15s, background 0.15s;
  }
  .channel-pill:hover { border-color: #265a88; background: #fff; }
  .channel-pill img { width: 20px; height: 20px; border-radius: 4px; flex-shrink: 0; }
  .channel-pill span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .channel-pill .bi { color: #6b7280; flex-shrink: 0; }

  .profile-loading { display: flex; gap: 18px; align-items: center; width: 100%; }
  .skeleton {
    background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 6px;
  }
  .skeleton-avatar { width: 64px; height: 64px; border-radius: 18px; flex-shrink: 0; }
  .skeleton-meta { flex: 1; display: flex; flex-direction: column; gap: 8px; }
  .skeleton-line { height: 14px; }
  .skeleton-line-lg { width: 50%; }
  .skeleton-line-sm { width: 30%; }
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .composer {
    margin-top: 18px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .composer-head {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 18px 22px;
    background: linear-gradient(135deg, #eaf1f9 0%, #fdf4ff 100%);
    border-bottom: 1px solid #e5e7eb;
  }
  .composer-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #ede9fe;
    color: #6d3fc4;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .composer-title { font-size: 15px; font-weight: 600; color: #0f172a; margin: 0 0 2px; }
  .composer-desc { font-size: 13px; color: #6b7280; margin: 0; }
  .composer-body { padding: 18px 22px 16px; }

  .composer-input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 14px;
    font-family: inherit;
    color: #0f172a;
    resize: vertical;
    min-height: 80px;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
  }
  .composer-input::placeholder { color: #64748b; }
  .composer-input:focus {
    outline: none;
    border-color: #265a88;
    box-shadow: 0 0 0 3px rgba(38, 90, 136, 0.12);
  }

  .quick-prompts {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  }
  .quick-prompt {
    background: #f8fafc;
    border: 1px dashed #d1d5db;
    color: #475569;
    border-radius: 999px;
    padding: 4px 12px;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: inherit;
  }
  .quick-prompt:hover {
    background: #eaf1f9;
    color: #265a88;
    border-color: #265a88;
    border-style: solid;
  }

  .settings-grid {
    margin-top: 16px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .settings-block {
    background: #fafbfc;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px 14px;
  }
  .settings-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 8px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .settings-label-sm {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
    color: #475569;
    font-size: 12.5px;
    margin: 0 0 6px;
  }
  .settings-hint {
    margin: 8px 0 0;
    font-size: 11.5px;
    color: #6b7280;
    line-height: 1.45;
  }

  .model-cost {
    font-weight: 700;
    font-size: 10.5px;
    letter-spacing: 0.04em;
    padding: 2px 7px;
    border-radius: 999px;
  }
  .cost-low     { color: #03543f; background: #def7ec; }
  .cost-medium  { color: #1e40af; background: #dbeafe; }
  .cost-high    { color: #92400e; background: #fef3c7; }
  .cost-highest { color: #9b1c1c; background: #fde8e8; }

  .model-options {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }
  .model-option {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 9px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 12px;
    color: #475569;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
    margin: 0;
    user-select: none;
  }
  .model-option input {
    appearance: none;
    -webkit-appearance: none;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 1.5px solid #cbd5e1;
    margin: 0;
    flex-shrink: 0;
    transition: border-color 0.15s, background 0.15s;
  }
  .model-option:hover { border-color: #265a88; color: #0f172a; }
  .model-option.is-active {
    border-color: #265a88;
    background: #eaf1f9;
    color: #265a88;
    font-weight: 600;
  }
  .model-option.is-active input {
    border-color: #265a88;
    background: radial-gradient(#265a88 38%, transparent 42%);
  }
  .model-option-badge {
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 1px 5px;
    border-radius: 999px;
    background: rgba(38, 90, 136, 0.08);
    color: #265a88;
  }
  .model-option.is-active .model-option-badge { background: rgba(38, 90, 136, 0.18); }

  .toggle-row { display: flex; flex-direction: column; gap: 8px; }
  .toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 13px;
    color: #334155;
    margin: 0;
  }
  .toggle input { position: absolute; opacity: 0; pointer-events: none; }
  .toggle-track {
    width: 30px;
    height: 17px;
    background: #cbd5e1;
    border-radius: 999px;
    position: relative;
    transition: background 0.15s;
    flex-shrink: 0;
  }
  .toggle-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 13px;
    height: 13px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.15s;
    box-shadow: 0 1px 2px rgba(0,0,0,0.15);
  }
  .toggle input:checked ~ .toggle-track { background: #265a88; }
  .toggle input:checked ~ .toggle-track .toggle-thumb { transform: translateX(13px); }
  .toggle.is-disabled { opacity: 0.55; cursor: not-allowed; }
  .toggle-text {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    line-height: 1.2;
  }
  .toggle-icon { width: 16px; height: 16px; border-radius: 4px; }
  .muted { color: #64748b; font-size: 11.5px; }

  .posts-limit { margin-top: 12px; padding-top: 12px; border-top: 1px dashed #e5e7eb; }
  .slider { width: 100%; accent-color: #265a88; }
  .slider-ticks {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
    color: #64748b;
    font-size: 10.5px;
  }

  .composer-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 14px;
    gap: 12px;
  }
  .composer-tip {
    color: #64748b;
    font-size: 12px;
  }
  .composer-tip kbd {
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-bottom-width: 2px;
    border-radius: 5px;
    padding: 1px 6px;
    font-size: 11px;
    color: #475569;
  }
  .btn-ask {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, #265a88 0%, #833ab4 100%);
    color: #fff;
    border: 0;
    border-radius: 9px;
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(38, 90, 136, 0.25);
    transition: transform 0.1s, box-shadow 0.15s, opacity 0.15s;
  }
  .btn-ask:hover:not(:disabled) {
    box-shadow: 0 6px 18px rgba(38, 90, 136, 0.35);
    transform: translateY(-1px);
  }
  .btn-ask:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-content { display: inline-flex; align-items: center; gap: 6px; }
  .spinner {
    display: inline-block;
    width: 13px;
    height: 13px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .conversation { margin-top: 28px; }
  .conversation-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #475569;
    margin: 0 0 14px;
  }
  .conversation-title .bi { color: #6b7280; }
  .conversation-count {
    background: #265a88;
    color: #fff;
    font-size: 11px;
    padding: 2px 7px;
    border-radius: 999px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .history { display: flex; flex-direction: column; gap: 14px; }

  .qa { display: flex; flex-direction: column; gap: 8px; }
  .qa-question { display: flex; flex-direction: column; gap: 6px; }
  .qa-bubble {
    border-radius: 14px;
    padding: 12px 16px;
    position: relative;
  }
  .qa-label {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #6b7280;
    margin-bottom: 4px;
  }
  .qa-label-err { color: #b91c1c; }
  .qa-bubble-user {
    background: #eaf1f9;
    border: 1px solid #c7dcf0;
    align-self: flex-start;
    max-width: 92%;
  }
  .qa-bubble-user p { margin: 0; color: #0f172a; font-size: 14px; line-height: 1.5; }
  .qa-bubble-ai {
    background: #fff;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .qa-meta {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: #64748b;
    padding-left: 4px;
  }
  .qa-model {
    color: #475569;
    background: #f1f5f9;
    padding: 1px 7px;
    border-radius: 4px;
  }
  .qa-meta-sep { color: #9ca3af; }

  .qa-thinking { display: flex; flex-direction: column; }
  .thinking-dots { display: inline-flex; gap: 4px; margin-top: 4px; }
  .thinking-dots span {
    width: 7px; height: 7px; border-radius: 50%;
    background: #265a88;
    opacity: 0.4;
    animation: thinking 1.4s ease-in-out infinite;
  }
  .thinking-dots span:nth-child(2) { animation-delay: 0.2s; }
  .thinking-dots span:nth-child(3) { animation-delay: 0.4s; }
  @keyframes thinking {
    0%, 80%, 100% { opacity: 0.35; transform: translateY(0); }
    40% { opacity: 1; transform: translateY(-3px); }
  }

  .qa-answer {
    color: #1f2937;
    font-size: 14px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  .qa-answer >>> strong { color: #0f172a; font-weight: 600; }
  .qa-answer >>> code {
    background: #f1f5f9;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 12.5px;
  }
  .qa-answer >>> a {
    color: #265a88;
    word-break: break-all;
  }
  .qa-answer >>> a:hover { text-decoration: underline; }
  .qa-answer >>> .cit-ref {
    color: #265a88;
    font-size: 10.5px;
    margin-left: 1px;
    font-weight: 600;
  }

  .qa-error-text { margin: 0; color: #b91c1c; font-size: 13.5px; }

  .qa-citations {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #e5e7eb;
  }
  .qa-citations-label {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7280;
    margin-bottom: 6px;
  }
  .qa-citations ol {
    margin: 0;
    padding-left: 18px;
    color: #475569;
    font-size: 12.5px;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .qa-citations a {
    color: #265a88;
    text-decoration: none;
    overflow-wrap: anywhere;
  }
  .qa-citations a:hover { text-decoration: underline; }

  @media (max-width: 720px) {
    .settings-grid { grid-template-columns: 1fr; }
    .influencer-card { flex-direction: column; align-items: flex-start; }
    .title-row { flex-direction: column; align-items: stretch; }
    .composer-foot { flex-direction: column; align-items: stretch; }
    .btn-ask { justify-content: center; }
  }
</style>



// WEBPACK FOOTER //
// src/components/influencer/InfluencerAsk.vue